import { MEDIA_QUERIES } from "@constants"
import styled from "styled-components"

export const PricingHeroPlanDIV = styled.div`
  // Free plan color
  --Plan--gradient-color: var(--color--green-100-rgb);
  --Plan--radial-gradient-color: var(--color--green-400-rgb);
  --Plan--border-padding: 6px;
  --Plan--gradient-border-radius: calc(var(--border-radius--L) * 0.4);

  background: var(--color--basics-white);
  border-radius: var(--border-radius--L);
  border: 1px solid rgba(var(--color--cobalt-900-rgb), 0.08);
  box-shadow: var(--elevation--S);
  display: flex;
  flex-direction: column;
  flex: 0 0 var(--PricingHeroPlan--card-width);
  gap: var(--spacing--base-1-5);
  padding: 25px 20px;
  position: relative;
  transition: all 0.2s;

  ${MEDIA_QUERIES.XL} {
    padding: var(--spacing--base-2);
  }

  &:hover {
    box-shadow: var(--elevation--M);
  }

  &::before {
    content: "";
    display: block;
    background: linear-gradient(to right, rgb(var(--Plan--gradient-color)), rgb(var(--Plan--radial-gradient-color)));
    border-radius: var(--Plan--gradient-border-radius) var(--Plan--gradient-border-radius) 0 0;
    height: var(--Plan--border-padding);
    left: var(--Plan--border-padding);
    position: absolute;
    top: var(--Plan--border-padding);
    width: calc(100% - var(--Plan--border-padding) * 2);
  }

  .gradient-background {
    border-radius: var(--Plan--gradient-border-radius);
    height: 325px;
    left: var(--Plan--border-padding);
    opacity: 0.5;
    overflow: hidden;
    position: absolute;
    top: var(--Plan--border-padding);
    width: calc(100% - var(--Plan--border-padding) * 2);
    z-index: 0;

    &::before,
    &::after {
      content: "";
      display: block;
      background:
        url("/images/noise-overlay.png"),
        radial-gradient(circle at 100% 0%, rgba(var(--Plan--radial-gradient-color), 0.45) 0%, rgba(var(--Plan--radial-gradient-color), 0) 33.3333%),
        rgba(var(--Plan--gradient-color), 1);
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }

    &::before {
      border: 0.5px solid rgba(var(--color--cobalt-900-rgb), 0.05);
      z-index: 1;
    }

    &::after {
      background: linear-gradient(180deg, rgba(var(--color--basics-white-rgb), 0), rgba(var(--color--basics-white-rgb), 1));
      z-index: 2;
    }
  }

  &.plan--free {
    --Plan--gradient-color: var(--color--clay-100-rgb);
    --Plan--radial-gradient-color: var(--color--clay-400-rgb);
  }

  &.plan--plus {
    --Plan--gradient-color: var(--color--cobalt-100-rgb);
    --Plan--radial-gradient-color: var(--color--cobalt-400-rgb);
  }

  &.plan--pro {
    --Plan--gradient-color: var(--color--purple-100-rgb);
    --Plan--radial-gradient-color: var(--color--purple-400-rgb);
  }

  &.is-disabled {
    --Plan--gradient-color: var(--color--charcoal-100-rgb);
    --Plan--radial-gradient-color: var(--color--charcoal-400-rgb);

    &,
    h3,
    h6 {
      color: var(--color--text-disabled);
    }
  }

  .active-plan--0 & {
    cursor: e-resize;

    &:nth-child(1) {
      cursor: default;
    }
  }

  .active-plan--1 & {
    cursor: e-resize;

    &:nth-child(1) {
      cursor: w-resize;
    }

    &:nth-child(2) {
      cursor: default;
    }
  }

  .active-plan--2 & {
    cursor: w-resize;

    &:nth-child(3) {
      cursor: default;
    }
  }

  @media (min-width: 635px) {
    flex: 0 0 var(--PricingHeroPlan--card-pct-width);
    cursor: default !important;
  }

  .plan-details,
  .plan-features {
    position: relative;
    z-index: 1;
  }

  .plan-description {
    text-wrap: balance;
  }

  .plan-price {
    display: flex;
    align-items: center;
    gap: 4px;

    span {
      margin: 0;
    }

    .plan-price--annual {
      line-height: calc(var(--font-size--5XL) * var(--line-height--tight));
    }

    .plan-price--mo {
      opacity: var(--opacity--60);
    }
  }

  &.is-disabled {
    .plan-price {
      flex-direction: column;
      align-items: flex-start;
      padding-left: 0;
    }
  }

  .plan-feature {
    display: flex;
    margin-bottom: var(--spacing--base-0-5);
    
    .feature-description {
      text-align: left;
      cursor: default;

      &[data-tooltip-id],
      &.is-more-link {
        // Copied from global [data-tooltip-id] style
        text-decoration: rgba(var(--color--text-default-rgb), 0.16) underline solid 1px;
        text-underline-offset: 0.3em;
      }

      &.is-more-link {
        cursor: pointer;
        color: var(--color--text-dark);

        &:hover {
          color: var(--color--action-default);
        }
      }
    }

    a {
      display: inline-block;
    }

    .Icon {
      display: block;
      margin-left: -0.45em;
      margin-right: 0.1em;
    }

    .Badge {
      display: inline-block;
      margin-left: 5px;
      position: relative;
      top: -0.3em;
    }
  }

  .add-on-badge-button {
    position: relative;

    .Icon {
      position: absolute;
      left: calc(100% + 10px);
      top: 50%;
      translate: 0 calc(-50% - 3px);
    }

    &.AI_DRAFTS {
      .Badge {
        position: relative;
        box-shadow: none;

        span {
          position: relative;
          z-index: 1;
          color: var(--color--purple-700);
          background: linear-gradient(150deg, rgba(40,53,219,1) 0%, rgba(173,74,251,1) 60%);
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        &::before,
        &::after {
          content: "";
          position: absolute;
          z-index: 0;
          border-radius: var(--border-radius--XS);
        }

        &::before {
          background: linear-gradient(214deg, rgba(255,169,119,1) 0%, rgba(255,133,218,1) 30%, rgba(125,119,255,1) 67%, rgba(104,236,230,1) 96%);
          width: calc(100% + 2px);
          height: calc(100% + 2px);
          top: -1px;
          left: -1px;
        }

        &::after {
          background: white;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }
      }
    }
  }

  .react-tooltip.react-tooltip {
    max-width: max(395px, calc((var(--container-width) / 12 * 4) - (var(--spacing--M) * 8 / 12)));
    text-wrap: balance;
    translate: 0 calc(var(--spacing--base-0-5) * -1);

    &.with-visual {

      .visual {
        margin: -10px -10px 8px;
        background: var(--color--slate-300);
        border-radius: calc(var(--border-radius--S) - 1px) calc(var(--border-radius--S) - 1px) 0 0;
        padding-top: var(--spacing--base-1-5);
      }

      .visual--text {
        padding: var(--spacing--S);
        color: var(--color--text-dark);
        text-align: left;
        display: none;
      }

      ${MEDIA_QUERIES.L} {
        max-width: max(505px, calc((var(--container-width) / 12 * 5) - (var(--spacing--M) * 7 / 12)));
      }

      .react-tooltip-arrow {
        background: var(--color--slate-300);
      }

      &.react-tooltip__place-top .react-tooltip-arrow {
        background: var(--color--charcoal-1200);
      }
    }

    ${MEDIA_QUERIES.M} {
      max-width: max(284px, calc((var(--container-width) / 12 * 3) - (var(--spacing--M) * 9 / 12)));
    }

    &.react-tooltip__place-bottom {
      translate: 0 var(--spacing--base-0-5);
    }
  }

  .Button.cta-button {
    width: 100%;
    margin-top: auto;
  }
`
